import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import styled from 'styled-components'

import { prepareMarkdownContent } from '@/utils/markdown'

type Props = {
  content?: string
  typography?: 'BodyMediumRegular' | 'BodyLargeRegular'
  color?: string
}

function LinkRenderer(props: any) {
  // Phone number support
  let href = props.href
  if (!props.href && props.children) {
    const text = props.children
    if (typeof text === 'string' && text.startsWith('+')) {
      href = `tel:${text?.replace(/\s/g, '')}`
    }
  }

  return (
    <a href={href} target='_blank' rel='noreferrer'>
      {props.children}
    </a>
  )
}

export const Markdown = ({ content, typography = 'BodyLargeRegular', color }: Props) => {
  const cont = prepareMarkdownContent(content)
  return (
    <StMarkdown typography={typography} $color={color} components={{ a: LinkRenderer }} remarkPlugins={[remarkGfm]}>
      {cont}
    </StMarkdown>
  )
}

const StMarkdown = styled(ReactMarkdown)<{
  typography: 'BodyMediumRegular' | 'BodyLargeRegular'
  $color?: string
}>`
  ${({ theme, typography }) => theme.Typography[typography]};
  color: ${({ theme, $color }) => $color ?? theme.Colors.Neutrals.Darker};

  strong {
    font-weight: 700;
  }
  em {
    font-style: italic;
  }

  p {
    ${({ theme, typography }) => theme.Typography[typography]};
    /* The typography mixin sets display: inline-block; force block so blocks
       stack instead of flowing onto the same line. */
    display: block;
    margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};
  }
  div {
    ${({ theme, typography }) => theme.Typography[typography]};
  }
  ul {
    ${({ theme, typography }) => theme.Typography[typography]};
    margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-left: 20px;
    list-style: disc;
    li {
      ${({ theme, typography }) => theme.Typography[typography]};
      list-style: disc;
      display: list-item;
    }
  }
  /* The global reset strips list markers, so numbered lists need their own
     decimal markers. Direct children only, so a nested bullet list keeps its
     discs. */
  ol {
    ${({ theme, typography }) => theme.Typography[typography]};
    margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-left: 20px;
    list-style: decimal;
    > li {
      ${({ theme, typography }) => theme.Typography[typography]};
      list-style: decimal;
      display: list-item;
    }
  }
  /* The editor wraps list-item content in a paragraph, which turndown
     serializes as a loose list. Strip the inner paragraph margin so list
     items stay tight (spacing comes from the list's own gap). */
  li > p {
    margin: 0;
  }
  a {
    ${({ theme, typography }) => theme.Typography[typography]};
    text-decoration: underline;
  }
  img {
    max-height: 400px;
  }
  /* Headings separate from the block above but sit close to the text they
     introduce, matching how they read in the editor. The typography mixin sets
     display: inline-block; force block so a heading and the text below it don't
     flow onto the same line. */
  h1 {
    ${({ theme }) => theme.Typography.Heading1};
    display: block;
    margin: ${({ theme }) => theme.UI.SpacingPx.Space6} 0
      ${({ theme }) => theme.UI.SpacingPx.Space2};
  }
  h2 {
    ${({ theme }) => theme.Typography.Heading2};
    display: block;
    margin: ${({ theme }) => theme.UI.SpacingPx.Space6} 0
      ${({ theme }) => theme.UI.SpacingPx.Space2};
  }
  h3 {
    ${({ theme }) => theme.Typography.Heading3};
    display: block;
    margin: ${({ theme }) => theme.UI.SpacingPx.Space6} 0
      ${({ theme }) => theme.UI.SpacingPx.Space2};
  }
  h4 {
    ${({ theme }) => theme.Typography.Heading4};
    display: block;
    margin: ${({ theme }) => theme.UI.SpacingPx.Space6} 0
      ${({ theme }) => theme.UI.SpacingPx.Space2};
  }
  h1:first-child,
  h2:first-child,
  h3:first-child,
  h4:first-child {
    margin-top: 0;
  }

  code {
    display: flex;
    width: 100%;
    overflow: auto;
    padding: 8px 12px;
    background: ${({ theme }) => theme.Colors.Neutrals.Lighter};
    border-radius: 4px;
  }
`
